import React, { useState } from "react";
import { Box, TextField, Typography } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import makeStyles from "@mui/styles/makeStyles";
import Button from "@/react/components/Common/Button";
import { callAPI } from "@/react/lib/common";
import { usePictureSets } from "@/react/Webshop/hooks/usePictureSets";
import WarningLastSubjectNotSelectedDialog from "@/react/Webshop/Components/WarningLastSubjectNotSelectedDialog";
import { Card } from "./Card";
const useStyles = makeStyles((theme) => ({
    button: {
        minWidth: 136,
        paddingLeft: 0,
        paddingRight: 0,
        whiteSpace: "nowrap",
        [theme.breakpoints.down("sm")]: {
            minWidth: 120,
        },
    },
}));
const AddAnotherSubjectCodeBox = ({ alertOnSameSubject = true, autoFocus = true, showTitle = true, }) => {
    if (!alertOnSameSubject) {
        return (React.createElement(AddAnotherSubjectCodeBoxContent, { alertOnSameSubject: false, autoFocus: autoFocus, currentPictureSetUuid: null, showTitle: showTitle }));
    }
    return (React.createElement(AddAnotherSubjectCodeBoxWithCurrentPictureSet, { autoFocus: autoFocus, showTitle: showTitle }));
};
const AddAnotherSubjectCodeBoxWithCurrentPictureSet = ({ autoFocus, showTitle, }) => {
    var _a, _b;
    const { currentPictureSet } = usePictureSets();
    const currentPictureSetUuid = (_b = (_a = currentPictureSet === null || currentPictureSet === void 0 ? void 0 : currentPictureSet.subject) === null || _a === void 0 ? void 0 : _a.uuid) !== null && _b !== void 0 ? _b : null;
    return (React.createElement(AddAnotherSubjectCodeBoxContent, { alertOnSameSubject: true, autoFocus: autoFocus, currentPictureSetUuid: currentPictureSetUuid, showTitle: showTitle }));
};
const AddAnotherSubjectCodeBoxContent = ({ alertOnSameSubject, autoFocus, currentPictureSetUuid, showTitle, }) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const [code, setCode] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const [warning, setWarning] = useState(null);
    const [warningRedirect, setWarningRedirect] = useState(null);
    const handleSubmit = (e) => {
        e.preventDefault();
        if (isLoading) {
            return;
        }
        const trimmedCode = code.trim();
        if (!trimmedCode) {
            setError(t("Please enter a code."));
            return;
        }
        if (trimmedCode.length < 5) {
            setError(t("Please enter the complete code."));
            return;
        }
        setIsLoading(true);
        setError("");
        setWarning(null);
        setWarningRedirect(null);
        callAPI("/ajax/prophoto/add_picture_set_by_code.php?code=" +
            encodeURIComponent(trimmedCode))
            .then((result) => {
            var _a;
            if (result.error || !result.valid) {
                setError(result.errorMessage || result.error || t("The code is not valid"));
                setIsLoading(false);
                return;
            }
            if (result.warning) {
                setWarning(result.warning);
                setWarningRedirect((_a = result.redirect) !== null && _a !== void 0 ? _a : null);
                setIsLoading(false);
                return;
            }
            if (alertOnSameSubject &&
                currentPictureSetUuid &&
                result.pictureSetUuid &&
                result.pictureSetUuid === currentPictureSetUuid) {
                window.alert(t("That subject didn't change, as it's the same subject."));
                setIsLoading(false);
                return;
            }
            if (result.redirect) {
                document.location.href = result.redirect;
                return;
            }
            setIsLoading(false);
        })
            .catch(() => {
            setIsLoading(false);
        });
    };
    const handleWarningOk = () => {
        if (warningRedirect) {
            document.location.href = warningRedirect;
            return;
        }
        setWarning(null);
    };
    if (warning) {
        return (React.createElement(WarningLastSubjectNotSelectedDialog, { subject: warning.payload.subject, handleOk: handleWarningOk }));
    }
    return (React.createElement(Card, null,
        showTitle && (React.createElement(Typography, { variant: "subtitle1", sx: {
                mb: 1.5,
                fontWeight: 500,
                color: "text.primary",
            } },
            React.createElement(Trans, null, "Add another subject code"))),
        React.createElement(Box, { component: "form", onSubmit: handleSubmit },
            React.createElement(Box, { sx: {
                    display: "flex",
                    gap: 1.5,
                    alignItems: "flex-start",
                } },
                React.createElement(TextField, { id: "simple-webshop-add-subject-code", label: t("Code"), variant: "outlined", value: code, onChange: (e) => {
                        setCode(e.target.value);
                        setError("");
                    }, error: !!error, helperText: error, autoFocus: autoFocus, size: "small", sx: {
                        flexGrow: 1,
                        minWidth: 0,
                    } }),
                React.createElement(Button, { variant: "primary", type: "submit", size: "small", disabled: isLoading, className: classes.button },
                    React.createElement(Trans, null, "Continue"))))));
};
export default AddAnotherSubjectCodeBox;
